import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  doc, 
  getDoc,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import Analytics from './analytics';


export interface CreateDecisionData {
  question: string;
  details?: string;
  requiresAuth?: boolean;
  visibility?: 'public' | 'unlisted';
}


export interface UpdateDecisionData {
  question: string;
  details?: string;
}


export const createDecision = async (data: CreateDecisionData): Promise<string> => {
  try {
    const auth = getAuth();
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }
    
    // Add character limit validation
    const CHARACTER_LIMITS = {
      question: 500,
      details: 2000
    };
    
    // Validate question length
    if (!data.question || data.question.length > CHARACTER_LIMITS.question) {
      throw new Error(`Question must be between 1 and ${CHARACTER_LIMITS.question} characters`);
    }
    
    // Validate details length (if provided)
    if (data.details && data.details.length > CHARACTER_LIMITS.details) {
      throw new Error(`Details must not exceed ${CHARACTER_LIMITS.details} characters`);
    }
    
    // Client-side rate limiting remains as a first defense
    const userId = auth.currentUser.uid;
    const today = new Date().toISOString().split('T')[0];
    const storageKey = `decisionCount_${userId}_${today}`;
    
    const storedCount = localStorage.getItem(storageKey) || '0';
    const count = parseInt(storedCount, 10);
    
    if (count >= 5) {
      throw new Error('You\'ve reached the daily limit for creating decisions');
    }
    
    const decision = {
      question: data.question,
      details: data.details,
      createdAt: serverTimestamp(),
      status: 'active',
      participants: 0,
      lastUpdated: serverTimestamp(),
      requiresAuth: data.requiresAuth ?? false,
      visibility: data.visibility || 'unlisted',
      createdBy: auth.currentUser?.uid,
      creatorName: auth.currentUser?.displayName || 'Anonymous'
    };
    
    // Use a transaction to ensure rate limiting is enforced before creating the decision
    const dayInMs = 24 * 60 * 60 * 1000;
    const currentDayTimestamp = Math.floor(Date.now() / dayInMs) * dayInMs;
    
    // Use a valid document path - rateLimits collection with rateLimit_{userId} as the document ID
    const rateLimitId = `decision_${userId}`;
    const rateLimitRef = doc(db, 'rateLimits', rateLimitId);
    
    // First, check the rate limit
    const rateLimitDoc = await getDoc(rateLimitRef);
    let currentCount = 0;
    
    if (rateLimitDoc.exists()) {
      const data = rateLimitDoc.data();
      // Only count if timestamp matches current day
      if (data.timestamp === currentDayTimestamp) {
        currentCount = data.count || 0;
      }
    }
    
    // Enforce server-side rate limit
    if (currentCount >= 5) {
      throw new Error('Rate limit exceeded: You\'ve reached the daily limit for creating decisions');
    }
    
    // Now create the decision
    const decisionRef = await addDoc(collection(db, 'decisions'), decision);
    
    // Update the rate limit counter
    await setDoc(rateLimitRef, {
      userId: userId,
      count: currentCount + 1,
      timestamp: currentDayTimestamp,
      expireAt: new Date(currentDayTimestamp + 2 * dayInMs) // TTL for auto-deletion
    });
    
    // Update client-side rate limiting
    localStorage.setItem(storageKey, (count + 1).toString());

    // Track decision creation in analytics
    Analytics.trackDecisionCreated(
      decisionRef.id, 
      data.question,
      data.requiresAuth ?? false,
      data.visibility ?? 'unlisted'
    );
    
    return decisionRef.id;
  } catch (error) {
    console.error('Error creating decision:', error);
    throw error;
  }
};


export const updateDecision = async (decisionId: string, data: UpdateDecisionData): Promise<void> => {
  try {
    const auth = getAuth();
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }
    
    const decisionRef = doc(db, 'decisions', decisionId);
    const decisionSnap = await getDoc(decisionRef);
    
    if (!decisionSnap.exists()) {
      throw new Error('Decision not found');
    }
    
    const decisionData = decisionSnap.data();
    
    // Check if current user is the creator
    if (decisionData.createdBy !== auth.currentUser.uid) {
      throw new Error('Only the creator can edit this decision');
    }
    
    // Check if there are participants
    if (decisionData.participants > 0) {
      throw new Error('Cannot edit a decision after participants have joined');
    }
    
    await updateDoc(decisionRef, {
      question: data.question,
      details: data.details || null,
      lastUpdated: serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error updating decision:', error);
    throw error;
  }
};